import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { FaCheck, FaPlus, FaMinus, FaClock, FaFileAlt, FaCertificate, FaShieldAlt, FaUserTie, FaChartLine } from 'react-icons/fa'

const DAOs = () => {
  const [openAccordion, setOpenAccordion] = useState<number | null>(0)

  const toggleAccordion = (index: number) => {
    setOpenAccordion(openAccordion === index ? null : index)
  }

  const services = [
    {
      icon: <FaClock className="text-4xl text-accent mb-4" />,
      title: 'Treasury Audits',
      description: 'Provide transparency to token holders with independent audits of DAO treasury holdings across multiple chains and protocols.'
    },
    {
      icon: <FaFileAlt className="text-4xl text-accent mb-4" />,
      title: 'Financial Reporting',
      description: 'Professional financial statements that help DAOs demonstrate legitimacy to partners, regulators, and potential investors.'
    },
    {
      icon: <FaCertificate className="text-4xl text-accent mb-4" />,
      title: 'Proof of Reserves',
      description: 'Real-time or periodic verification of DAO treasury assets to build trust with your community and stakeholders.'
    },
    {
      icon: <FaShieldAlt className="text-4xl text-accent mb-4" />,
      title: 'Smart Contract Audits',
      description: 'Security audits and formal verification of your DAO\'s smart contracts to protect treasury assets and governance mechanisms.'
    },
    {
      icon: <FaUserTie className="text-4xl text-accent mb-4" />,
      title: 'Governance Advisory',
      description: 'Expert guidance on implementing transparent governance structures and financial controls for decentralized organizations.'
    },
    {
      icon: <FaChartLine className="text-4xl text-accent mb-4" />,
      title: 'Tokenomics Analysis',
      description: 'Independent analysis and verification of token distribution, vesting schedules, and economic sustainability.'
    },
  ]

  const growthItems = [
    {
      title: 'Build Credibility with Token Holders and Community Members',
      content: 'Demonstrate responsible stewardship of community assets through regular, transparent financial reporting and independent audits.'
    },
    {
      title: 'Attract Strategic Partnerships and Institutional Support',
      content: 'Professional financial reporting and controls make your DAO attractive to traditional organizations and institutional investors.'
    },
    {
      title: 'Navigate Regulatory Uncertainty with Confidence',
      content: 'Stay ahead of evolving regulations by implementing professional financial controls and transparent reporting from day one.'
    },
    {
      title: 'Scale Operations While Maintaining Decentralization',
      content: 'Implement professional financial infrastructure that supports growth without compromising your DAO\'s decentralized ethos.'
    },
  ]

  return (
    <>
      <Helmet>
        <title>DAO Treasury Audit Services - Proof of Reserves Auditor</title>
        <meta name="description" content="Professional audit and financial services for DAOs, network states, and decentralized organizations." />
      </Helmet>

      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary via-primary-dark to-primary text-white py-20 overflow-hidden">
          {/* Animated Particle Wave Background */}
          <div className="absolute inset-0 opacity-30">
            <svg className="absolute bottom-0 w-full h-auto" viewBox="0 0 1280 400" preserveAspectRatio="none">
              <defs>
                <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#4A9BFF" stopOpacity="0.3" />
                  <stop offset="50%" stopColor="#4A9BFF" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="#4A9BFF" stopOpacity="0.3" />
                </linearGradient>
              </defs>

              {/* Multiple wave layers for depth */}
              <path
                d="M0,200 Q320,150 640,200 T1280,200 L1280,400 L0,400 Z"
                fill="url(#waveGradient)"
                opacity="0.4"
              >
                <animate
                  attributeName="d"
                  dur="8s"
                  repeatCount="indefinite"
                  values="
                    M0,200 Q320,150 640,200 T1280,200 L1280,400 L0,400 Z;
                    M0,200 Q320,250 640,200 T1280,200 L1280,400 L0,400 Z;
                    M0,200 Q320,150 640,200 T1280,200 L1280,400 L0,400 Z"
                />
              </path>

              <path
                d="M0,240 Q320,190 640,240 T1280,240 L1280,400 L0,400 Z"
                fill="url(#waveGradient)"
                opacity="0.3"
              >
                <animate
                  attributeName="d"
                  dur="10s"
                  repeatCount="indefinite"
                  values="
                    M0,240 Q320,190 640,240 T1280,240 L1280,400 L0,400 Z;
                    M0,240 Q320,290 640,240 T1280,240 L1280,400 L0,400 Z;
                    M0,240 Q320,190 640,240 T1280,240 L1280,400 L0,400 Z"
                />
              </path>

              {/* Dotted particle effect */}
              <g opacity="0.6">
                {[...Array(50)].map((_, i) => {
                  const x = (i * 25.6) % 1280;
                  const y = 150 + (i * 13) % 200;
                  return (
                    <circle
                      key={i}
                      cx={x}
                      cy={y}
                      r="2"
                      fill="#4A9BFF"
                      opacity="0.5"
                    >
                      <animate
                        attributeName="opacity"
                        values="0.3;0.8;0.3"
                        dur={`${3 + (i % 4)}s`}
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="cy"
                        values={`${y};${y - 20};${y}`}
                        dur={`${4 + (i % 3)}s`}
                        repeatCount="indefinite"
                      />
                    </circle>
                  )
                })}
              </g>
            </svg>
          </div>
          
          <div className="container-custom relative z-10">
            <div className="max-w-4xl">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                DAOs & On-Chain Organizations
                <br />
                <span className="text-accent">Financial Services</span>
              </h1>
              <p className="text-2xl text-gray-200 mb-8">
                Professional financial infrastructure for decentralized organizations
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <FaCheck className="text-accent mt-1 flex-shrink-0" />
                  <p className="text-lg">
                    Build Trust Through Transparent Treasury Management
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <FaCheck className="text-accent mt-1 flex-shrink-0" />
                  <p className="text-lg">
                    Demonstrate Financial Responsibility to Your Community
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <FaCheck className="text-accent mt-1 flex-shrink-0" />
                  <p className="text-lg">
                    Scale Operations with Professional Financial Controls
                  </p>
                </div>
              </div>

              <Link
                to="/contact"
                className="inline-block bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors"
              >
                TALK TO AN EXPERT
              </Link>
            </div>
          </div>
        </section>

        {/* Why DAOs Need Professional Financial Services */}
        <section className="py-16 bg-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-4xl font-bold text-primary mb-6">
                Why DAOs Need Professional Financial Services
              </h2>
              <p className="text-xl text-gray-600">
                As DAOs manage billions in treasury assets and interact with traditional institutions, professional financial infrastructure is no longer optional—it's essential for growth and sustainability.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-primary mb-4">The Challenge</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-3">
                    <span className="text-accent">•</span>
                    <span>Managing multi-million dollar treasuries without traditional financial controls</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-accent">•</span>
                    <span>Building trust with diverse stakeholder groups</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-accent">•</span>
                    <span>Navigating unclear regulatory requirements</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-accent">•</span>
                    <span>Attracting institutional partnerships and funding</span>
                  </li>
                </ul>
              </div>

              <div className="bg-accent/5 p-8 rounded-lg border-2 border-accent">
                <h3 className="text-2xl font-bold text-primary mb-4">The Solution</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-3">
                    <FaCheck className="text-accent mt-1 flex-shrink-0" />
                    <span>Independent audits and attestations of treasury holdings</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <FaCheck className="text-accent mt-1 flex-shrink-0" />
                    <span>Professional financial reporting and transparency</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <FaCheck className="text-accent mt-1 flex-shrink-0" />
                    <span>Proactive compliance and governance frameworks</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <FaCheck className="text-accent mt-1 flex-shrink-0" />
                    <span>Credibility that opens doors to partnerships</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-16 bg-gray-50">
          <div className="container-custom">
            <h2 className="text-4xl font-bold text-center text-accent mb-12">
              Tailored Services for DAOs
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {services.map((service, index) => (
                <div key={index} className="bg-white p-6 rounded-lg hover:shadow-xl transition-shadow">
                  {service.icon}
                  <h3 className="text-xl font-bold mb-3 text-primary">{service.title}</h3>
                  <p className="text-gray-600 text-sm">{service.description}</p>
                </div>
              ))}
            </div>

            <div className="text-center">
              <Link
                to="/services"
                className="inline-block bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors"
              >
                EXPLORE ALL SERVICES
              </Link>
            </div>
          </div>
        </section>

        {/* Growth Accordion */}
        <section className="py-16 bg-white">
          <div className="container-custom">
            <h2 className="text-4xl font-bold text-center text-accent mb-12">
              Unlock Your DAO's Next Stage of Growth
            </h2>
            
            <div className="max-w-4xl mx-auto space-y-4">
              {growthItems.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleAccordion(index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-lg font-semibold text-gray-900 pr-4">{item.title}</span>
                    {openAccordion === index ? (
                      <FaMinus className="text-accent flex-shrink-0" />
                    ) : (
                      <FaPlus className="text-accent flex-shrink-0" />
                    )}
                  </button>
                  
                  {openAccordion === index && (
                    <div className="px-6 pb-6 text-gray-700">
                      <p>{item.content}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Case Study */}
        <section className="py-16 bg-gray-50">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto bg-white rounded-lg p-8 md:p-12 shadow-lg">
              <span className="inline-block bg-accent text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
                Success Story
              </span>
              <h2 className="text-3xl font-bold text-primary mb-6">
                How Professional Audits Helped a DeFi DAO Secure $50M in Partnerships
              </h2>
              <p className="text-lg text-gray-700 mb-4">
                A leading DeFi DAO struggled to attract institutional partnerships despite a $200M treasury. After implementing regular treasury audits and professional financial reporting, they:
              </p>
              <ul className="space-y-3 text-gray-700 mb-6">
                <li className="flex items-start gap-3">
                  <FaCheck className="text-accent mt-1 flex-shrink-0" />
                  <span>Secured partnerships with three major institutional investors</span>
                </li>
                <li className="flex items-start gap-3">
                  <FaCheck className="text-accent mt-1 flex-shrink-0" />
                  <span>Increased community participation in governance by 40%</span>
                </li>
                <li className="flex items-start gap-3">
                  <FaCheck className="text-accent mt-1 flex-shrink-0" />
                  <span>Established relationships with traditional financial institutions</span>
                </li>
              </ul>
              <p className="text-lg text-gray-700 italic">
                "Professional financial infrastructure transformed how partners view our organization. We're now treated as a serious financial entity, not just an experiment."
              </p>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-br from-primary to-primary-dark text-white">
          <div className="container-custom text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-accent">
              Ready to Professionalize Your DAO?
            </h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Let's discuss how professional financial services can help your DAO build trust, attract partnerships, and scale sustainably.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors"
              >
              START THE CONVERSATION
            </Link>
          </div>
        </section>
      </div>
    </>
  )
}

export default DAOs